import { Button } from "@material-ui/core";
import React from "react";
import { DesktopHotelPhotoModalConnectorProps } from "./container";

import { STAYS_WISHLIST } from "@capone/experiments";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import {
  ActionButton,
  DesktopPopupModal,
  getImagesArray,
  HotelName,
  HotelStarRating,
  HotelTotalPanel,
  Icon,
  IconName,
  MultiroomSuffixText,
  PhotoLayout,
} from "halifax";
import { StayType } from "redmond/hotels-module/interfaces";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import { WishlistToggleHeart } from "../../../wishlist/components/WishlistToggleHeart/component";
import "./styles.scss";

export interface DesktopHotelPhotoModalProps
  extends DesktopHotelPhotoModalConnectorProps {
  isSkeleton?: boolean;
}

export const DesktopHotelPhotoModal = (props: DesktopHotelPhotoModalProps) => {
  const {
    roomInfoProducts,
    lodging,
    roomProduct,
    nightCount,
    isSkeleton,
    rewardsKey,
    viewedHotelDetailsProperties,
  } = props;
  const [imagesArray, setImagesArray] = React.useState<string[]>([]);
  const [openModal, setOpenModal] = React.useState<boolean>(false);

  const expState = useExperiments();
  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  React.useEffect(() => {
    getImagesArray(lodging?.lodging.media ?? [], setImagesArray);
  }, [lodging, setImagesArray]);

  const filteredImagesArray = React.useMemo(
    () =>
      // The filter method is used to remove empty items in imagesArray
      imagesArray.filter((imageTuple) => !!imageTuple),
    [imagesArray]
  );

  return (
    <div className="desktop-hotel-photo-modal">
      {isSkeleton && (
        <Skeleton className="desktop-hotel-photo-modal-main-image-container skeleton" />
      )}
      {!isSkeleton && (
        <div
          className="desktop-hotel-photo-modal-main-image-container"
          onClick={() => {
            trackEvent({
              eventName: "hotel_shop_image_modal",
              properties: { ...viewedHotelDetailsProperties },
            });
            setOpenModal(!openModal);
          }}
        >
          <div className="desktop-hotel-photo-modal-main-image-section-left">
            <img
              className="main-image-left"
              src={filteredImagesArray[0]}
              alt="hotel-1"
            />
          </div>
          <div className="desktop-hotel-photo-modal-main-image-section-right">
            <div className="main-image-right top-image">
              <img src={filteredImagesArray[1]} alt="hotel-2" />
            </div>
            <div className="main-image-right bottom-image">
              <img src={filteredImagesArray[2]} alt="hotel-3" />
            </div>
          </div>
          <Button
            className="show-all-photos-button"
            onClick={() => {
              setOpenModal(true);
            }}
          >
            <Icon name={IconName.ShowAllImagesIcon} />
            <span className="show-all-photos-button-label">
              See all photos ({filteredImagesArray.length})
            </span>
          </Button>
          {isWishlistExperiment && lodging && lodging.lodging.id && (
            <WishlistToggleHeart
              listingId={lodging?.lodging?.id}
              listingImageUrl={lodging?.lodging?.media?.[0]?.url}
              stayType={StayType.Hotels}
              lodgingCollection={lodging?.lodgingCollection}
              style={{
                position: "absolute",
                top: 32 + 10,
                right: 10,
              }}
            />
          )}
        </div>
      )}
      <DesktopPopupModal
        className={clsx("desktop-hotel-photo-modal-popup", "b2b")}
        open={openModal}
        invisibleBackdrop={false}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();
          setOpenModal(false);
        }}
      >
        <div
          id="desktop-hotel-photo-modal-container"
          className="desktop-hotel-photo-modal-container"
        >
          <div id="photo-gallery" className="photo-gallery">
            <PhotoLayout
              imgUrls={filteredImagesArray}
              scrollableTarget="photo-gallery"
            />
          </div>
          <div className="photo-gallery-hotel-info">
            <HotelStarRating
              starRating={lodging?.lodging?.starRating}
              city={lodging?.lodging?.city}
              isSkeleton={isSkeleton}
            />
            <HotelName name={lodging?.lodging?.name} isSkeleton={isSkeleton} />
            {!!roomProduct && !!lodging && (
              <HotelTotalPanel
                chosenRate={roomProduct}
                selectedLodging={lodging}
                cheapestProduct={roomInfoProducts[0]?.products[0]}
                nightCount={nightCount || undefined}
                rewardsKey={rewardsKey}
                useRedesign
                suffixText={
                  <MultiroomSuffixText
                    className="availability-suffix-text"
                    roomsCount={props.roomsCount}
                  />
                }
              />
            )}
            <ActionButton
              className="desktop-photo-modal-choose-room-cta"
              onClick={() => {
                trackEvent({
                  eventName: "select_room_from_main_image",
                  properties: { ...viewedHotelDetailsProperties },
                });
                setOpenModal(false);
                setTimeout(() => {
                  document
                    .getElementsByClassName("reserve-cta")[0]
                    ?.scrollIntoView({ behavior: "smooth", block: "end" });
                }, 300);
              }}
              message={<span className="button-text">Choose Room</span>}
            />
          </div>
        </div>
      </DesktopPopupModal>
    </div>
  );
};
